import { FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMDatePicker from "src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import ATMTextArea from "src/components/UI/atoms/formFields/ATMTextArea/ATMTextArea";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import useAccounts from "src/hooks/useAccounts";
import { InstallmentPaymentValues } from "src/models/InstallmentPayment.model";
import { paymentModeOptions } from "src/utils/paymentModeOptions";
import { getCouponDiscount } from "./InstallmentPaymentWrapper";
import { RxCross2 } from "react-icons/rx";
import { useEffect } from "react";

type Props = {
  formikProps: FormikProps<InstallmentPaymentValues>;
  onClose: () => void;
  handleCouponApply: (couponCode: string) => void;
  isCouponDetailsLoading: boolean;
};

const InstallmentPayment = ({
  formikProps,
  onClose,
  handleCouponApply,
  isCouponDetailsLoading,
}: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;

  const { accounts, isDataLoading: isAccountsLoading } = useAccounts(); 

  useEffect(()=>{
  setFieldValue('netReceivedAmount' ,     getCouponDiscount(
    values.installmentAmount,
    values?.couponData?.discountType || "FIXED",
    values?.couponData?.discountAmount || "0",
    values?.couponData?.maxDiscountAmount || "0"
  ).discountedAmount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[values])

  return (
    <div className="p-4 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium"> Receive Payment </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Date */}
        <div className="">
          <ATMDatePicker
            name="date"
            value={values.date || null}
            onChange={(newValue) => setFieldValue("date", newValue)}
            label="Date"
          />
        </div>

        {/* Installment Amount */}
        <div className="">
          <ATMTextField
            name="installmentAmount"
            value={values.installmentAmount}
            onChange={(e) => {}}
            label="Installment Amount"
            placeholder="Enter Installment Amount"
            onBlur={handleBlur}
            disabled
          />
        </div>

        {/* Coupon Code */}
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <ATMTextField
              name="couponCode"
              value={values.couponCode}
              onChange={(e) => setFieldValue("couponCode", e.target.value)}
              label="Coupon Code"
              placeholder="Enter Coupon Code"
              onBlur={handleBlur}
            />
          </div>

          <div className="w-[60px]">
            {values?.couponData ? (
              <ATMLoadingButton
                onClick={() => {
                  setFieldValue("couponData", null);
                  setFieldValue("couponCode", "");
                }}
                className="bg-red-500"
              >
                <RxCross2 size={20} className="font-bold" />
              </ATMLoadingButton>
            ) : (
              <ATMLoadingButton
                onClick={() => {
                  setFieldValue("couponData", null);
                  handleCouponApply(values?.couponCode);
                }}
                disabled={!values?.couponCode}
                isLoading={isCouponDetailsLoading}
              >
                {" "}
                Apply{" "}
              </ATMLoadingButton>
            )}
          </div>
        </div>

        {/* Coupon Discount */}
        <div className="">
          <ATMTextField
            name=""
            value={`${
              getCouponDiscount(
                values.installmentAmount,
                values?.couponData?.discountType || "FIXED",
                values?.couponData?.discountAmount || "0",
                values?.couponData?.maxDiscountAmount || "0"
              ).discountAmount
            }`}
            onChange={(e) => {}}
            label="Coupon Discount"
            placeholder="Enter Coupon Discount"
            onBlur={handleBlur}
            disabled
          />
        </div>

        {/*Received Amount */}
        <div className="">
          <ATMTextField
            name="netReceivedAmount"
            value={`${
              getCouponDiscount(
                values.installmentAmount,
                values?.couponData?.discountType || "FIXED",
                values?.couponData?.discountAmount || "0",
                values?.couponData?.maxDiscountAmount || "0"
              ).discountedAmount
            }`}
            onChange={(e) => {}}
            label="Net Receivable Amount"
            placeholder="Enter Amount"
            onBlur={handleBlur}
            disabled
          />
        </div>

        {/* Account */}
        <div className="">
          <ATMSelect
            name="account"
            value={values.account}
            onChange={(newValue) => setFieldValue("account", newValue)}
            onBlur={handleBlur}
            label="Account"
            placeholder="Select Account"
            options={accounts?.map((account) => ({
              label: account.accountName,
              value: account._id,
            }))}
            isLoading={isAccountsLoading}
          />
        </div>

        {/* Payment Mode */}
        <div className="">
          <ATMSelect
            name="paymentMode"
            value={values.paymentMode}
            onChange={(newValue) => setFieldValue("paymentMode", newValue)}
            onBlur={handleBlur}
            label="Payment Mode"
            placeholder="Select Payment Mode"
            options={paymentModeOptions}
          />
        </div>

        {/* Payment Remark */}
        <div className="col-span-full">
          <ATMTextArea
            name="paymentRemark"
            value={values.paymentRemark}
            onChange={(newValue) => setFieldValue("paymentRemark", newValue)}
            label="Payment Remark"
            placeholder="Enter Payment Remark"
            onBlur={handleBlur}
          />
        </div>
      </div>
      <div>
        <ATMLoadingButton
          isLoading={isSubmitting}
          type="submit"
          disabled={isCouponDetailsLoading}
        >
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default InstallmentPayment;
